import { cloneDeep, find, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  getItemsKey,
  IDashboardChartData,
  IDashboardHomeCardMetrics,
  IDashboardLiveData,
  queryHomeDashboardLiveData,
} from '~/src/dash/api/dashboards/home';
import { getSmallBiddersList } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesWithBidderTypes } from '~/src/dash/api/filters/demandSources';
import { DashboardCardsV2 } from '~/src/dash/components/cards/DashboardCardsV2';
import { IDashboardChartMetric, mergeCompareData } from '~/src/dash/components/charts/common';
import { CompareChart } from '~/src/dash/components/charts/CompareChart';
import {
  FILTER_PILLS,
  FilterPillKey,
  formatSelectedBidderGroupIntoStringOfIDs,
  formatSelectedChannelGroupIntoStringOfIDs,
  formatSelectedDestinationsIntoStringOfSiteIDs,
  formatSelectedRouteIntoStringOfSiteIDs,
  getDashboardFilterParams,
  getInitialDashboardFilterParams,
} from '~/src/dash/components/filters/common';
import { ClockIcon, RefreshCycleIcon } from '~/src/dash/components/icons/misc-icons';
import { IconTextToggle } from '~/src/dash/components/toggles/IconTextToggle';
import { MenuToggle } from '~/src/dash/components/toggles/MenuToggle';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { defaultTimeRanges, findTimeRangeByKey, TimeRange } from '~/src/dash/utils/dateTimeRanges';
import { useInterval } from '~/src/dash/utils/hooks';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { getTimezone } from '~/src/dash/utils/timezone';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  addDefaultSelected,
  clearedActiveFilters,
  DashboardLiveCardGroup,
  extractDataForMultiLineChart,
  extractViableLiveMetrics,
  generateInventoryRoutesAndDestinations,
  getLiveCardGroup,
  isBidderTypeSelected,
  numSitesBidders,
  refreshBiddersFilter,
  refreshDemandSourcesFilter,
  updateActiveChartMetrics,
  updateDefaultSelectedForFilters,
  updateDisplayedMetricKeys,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DashboardFilterMenuToggle } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilterMenuToggle';
import { DashboardFilters } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardFilters';
import { DashboardSelector } from '~/src/dash/views/dashboards/DashboardHome/common/DashboardSelector';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { DashboardSubChart } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardLive/DashboardSubChart';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const StaticAutoRefresh = ({ lastRefreshedTime = '' }) => {
  return (
    <TooltipWithTranslation
      tKey={lastRefreshedTime ? 'DashboardTooltipLastUpdatedTime' : ''}
      tProps={{ timestamp: lastRefreshedTime }}
    >
      <div>
        <Cols
          gap={spacing.extraSmall}
          style={{
            alignItems: 'center',
            backgroundColor: publicaColors.lightGray,
            borderRadius: '4px',
            padding: '2px 10px 2px 8px',
          }}
        >
          <Col displayFlex>
            <RefreshCycleIcon color={publicaColors.gray} />
          </Col>
          <Col>
            <Text small muted disableSelect tKey='AutoRefresh'>
              Auto-Refresh
            </Text>
          </Col>
        </Cols>
      </div>
    </TooltipWithTranslation>
  );
};

// IMPORTANT: IF YOU ARE UPDATING LIVE, CHECK IF PUBLICA ANALYTICS MUST BE UPDATED TOO
// IMPORTANT: PLEASE READ THE README BEFORE EDITING -> https://publica-project.atlassian.net/wiki/x/DQDXpQ
export const DashboardLive = () => {
  const initialLoadStart = moment().unix();
  const { Dashboards } = useBaseConfig();
  const overviewCfg = Dashboards.Overview;
  const liveCfg = Dashboards.Live;
  const { defaultState, cards, subCharts, chartSyncID } = liveCfg;
  const { session, user, currencyType, isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();

  const initialActiveFilters = overviewCfg.defaultState.activeFilters;
  if (
    !initialActiveFilters.demand &&
    !initialActiveFilters.supply &&
    !initialActiveFilters.device &&
    !isEmpty(overviewCfg.filters['Outbound Deals'].defaultSelected)
  ) {
    initialActiveFilters.deal = 'Outbound Deals';
  } else {
    overviewCfg.filters['Outbound Deals'].defaultSelected = [];
  }
  const initialCardGroup = getLiveCardGroup(overviewCfg.defaultState.activeFilters);
  const initialDisplayedMetricKeys = updateDisplayedMetricKeys(
    cards.groupings[initialCardGroup],
    defaultState.displayedMetricKeys,
  );
  const initialActiveChartMetrics = updateActiveChartMetrics(
    initialDisplayedMetricKeys,
    defaultState.activeChartMetrics,
    overviewCfg.cards.metrics,
  );
  const defaultTimezone =
    overviewCfg.defaultState.timezone ||
    getTimezone(user.user.Timezone, session.publisher.Timezone);

  const isMounted = useRef(false);
  const isInitialized = useRef(false);
  const hasInitialQueryCompleted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [timeRange, setTimeRange] = useState(findTimeRangeByKey(defaultState.timeRange));
  const [displayedMetricKeys, setDisplayedMetricKeys] = useState(initialDisplayedMetricKeys);
  const [activeChartMetrics, setActiveChartMetrics] = useState(initialActiveChartMetrics);
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState({} as Record<string, IMenuList>);
  const [selectedFilters, setSelectedFilters] = useState({} as Record<string, IMenuList>);
  const [isDemandRefreshing, setIsDemandRefreshing] = useState(false);
  const [activeFilters, setActiveFilters] = useState(overviewCfg.defaultState.activeFilters);
  const [cardGroup, setCardGroup] = useState(initialCardGroup);
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(
    overviewCfg.defaultState.isPeriodMenuOpen,
  );
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(
    overviewCfg.defaultState.isFilterMenuOpen,
  );
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [data, setData] = useState({} as IDashboardLiveData);
  const [isDataRefreshActive, setIsDataRefreshActive] = useState(false);
  const [lastRefreshedTime, setLastRefreshedTime] = useState('');

  const isDemandSourceUsingBidderType = useRef(false);

  useEffect(() => {
    getDashboardLiveData();
    getFilters();
    track('Dash Live - View', { isSuperUser }, user.user);
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setIsDataRefreshActive(false);
      getDashboardLiveData();
    }
  }, [timeRange]);

  useEffect(() => {
    if (isMounted.current && filtersLoadState === LoadingStateHasData && isInitialized.current) {
      setIsDataRefreshActive(false);
      getDashboardLiveData();
    }
  }, [selectedFilters]);

  useInterval(
    () => {
      getDashboardLiveData(true);
    },
    isDataRefreshActive ? 60 * 1000 : null,
  );

  useEffect(() => {
    if (isMounted.current) {
      updateDefaultSelectedForFilters(
        selectedFilters,
        overviewCfg.filters,
        overviewCfg.defaultState,
      );
      if (!isInitialized.current) isInitialized.current = true;
    }
    isMounted.current = true;
  }, [selectedFilters]);

  const handleToggleTimeRange = (rangeKey: TimeRange) => {
    setTimeRange(findTimeRangeByKey(rangeKey));
    track('Dash Live - Select Time Shortcut', { isSuperUser, range: rangeKey }, user.user);
  };

  const getDashboardLiveData = (isRefreshCall?: boolean) => {
    const queryStart = moment().unix();
    if (!isRefreshCall) {
      setLoadState(LoadingStateLoading);
    }

    const hasInitiallyLoaded = isInitialized.current && filtersLoadState !== LoadingStateLoading;

    let isUsingBidderType = false;
    if (activeFilters.demand === 'Filtered Demand Sources') {
      if (hasInitiallyLoaded) {
        isUsingBidderType = isBidderTypeSelected(selectedFilters['Filtered Demand Sources']);
      } else {
        isUsingBidderType = overviewCfg.defaultState.isDemandSourceUsingBidderType;
      }
    }
    isDemandSourceUsingBidderType.current = isUsingBidderType;

    const liveItemsKey = getItemsKey(activeFilters, true, isUsingBidderType);

    const updatedCardGroup = getLiveCardGroup(activeFilters);

    const filterParams = hasInitiallyLoaded
      ? getDashboardFilterParams(
          selectedFilters,
          overviewCfg.filters,
          activeFilters,
          isUsingBidderType,
        )
      : getInitialDashboardFilterParams(overviewCfg.filters, activeFilters, isUsingBidderType);

    const numSiteBidderKeys = numSitesBidders(
      filterParams.site_ids || '',
      filterParams.bidder_ids || '',
    );
    const isMaxBucketsExceeded = numSiteBidderKeys > 5000;
    if (isMaxBucketsExceeded) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(
          `The query could not be completed. Only a maximum of 5000 combinations of bidders and sites can be queried with live data (last count: ${numSiteBidderKeys})`,
        );
        setSnackbarError(true);
        setData({
          LiveData: [] as IDashboardChartData[],
          CardMetrics: {} as IDashboardHomeCardMetrics,
        } as IDashboardLiveData);
        setLoadState(LoadingStateNoData);
        setIsDataRefreshActive(false);
        if (cardGroup !== updatedCardGroup) updateMetrics(updatedCardGroup);
      });
      return;
    }

    const query = {
      publisher_id: session.publisher.ID,
      timezone,
      currency_type: currencyType,
      live_density_range: timeRange.value,
      live_items_key: liveItemsKey,
      ...filterParams,
    };
    queryHomeDashboardLiveData(session.publisher.ID, query)
      .then((result) => {
        const isDataEmpty = isEmpty(result.LiveData);
        unstable_batchedUpdates(() => {
          setData(result);
          setLoadState(isDataEmpty ? LoadingStateNoData : LoadingStateHasData);
          setIsDataRefreshActive(true);
          setLastRefreshedTime(moment().format('HH:mm:ss'));
          if (cardGroup !== updatedCardGroup) updateMetrics(updatedCardGroup);
          if (isDataEmpty) {
            setSnackbarError(false);
            setSnackbarMessage(t('SnackBarNoLiveDataFound'));
          }
        });
        if (!hasInitialQueryCompleted.current) {
          track(
            'Dash Live - Main Data Initial Query',
            {
              isSuperUser,
              initialLoadTime: moment().unix() - initialLoadStart,
              activeFilters,
              timeRange: timeRange.label,
            },
            user.user,
          );
          hasInitialQueryCompleted.current = true;
        } else {
          if (!isRefreshCall) {
            track(
              'Dash Live - Main Data Query',
              {
                isSuperUser,
                queryTime: moment().unix() - queryStart,
                activeFilters,
                timeRange: timeRange.label,
              },
              user.user,
            );
          }
        }
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          if (!isDataRefreshActive) {
            setData({
              LiveData: [] as IDashboardChartData[],
              CardMetrics: {} as IDashboardHomeCardMetrics,
            } as IDashboardLiveData);
          }
          setLoadState(LoadingStateNoData);
          if (cardGroup !== updatedCardGroup) updateMetrics(updatedCardGroup);
          setSnackbarError(true);
          setSnackbarMessage(
            !isDataRefreshActive
              ? t('SnackbarErrorRetrievingData')
              : t('DashboardSnackbarErrorRefreshingData', { timestamp: lastRefreshedTime }),
          );
        });
      });
  };

  const updateMetrics = (updatedCardGroup: DashboardLiveCardGroup) => {
    const updatedDisplayedMetricKeys = updateDisplayedMetricKeys(
      cards.groupings[updatedCardGroup],
      displayedMetricKeys,
    );
    const updatedActiveChartMetrics = updateActiveChartMetrics(
      updatedDisplayedMetricKeys,
      activeChartMetrics,
      overviewCfg.cards.metrics,
    );
    unstable_batchedUpdates(() => {
      setDisplayedMetricKeys(updatedDisplayedMetricKeys);
      setCardGroup(updatedCardGroup);
      setActiveChartMetrics(updatedActiveChartMetrics);
    });
    defaultState.displayedMetricKeys = updatedDisplayedMetricKeys;
    defaultState.activeChartMetrics = updatedActiveChartMetrics;
  };

  // load all filters
  const getFilters = () => {
    setFiltersLoadState(LoadingStateLoading);
    let updatedFilters = {} as Record<FilterPillKey, IMenuList>;
    const updatedSelectedFilters = {} as Record<FilterPillKey, IMenuList>;
    const apiCalls = [] as Array<Promise<Record<string, IMenuList>>>;

    const filterKeys = liveCfg.orderedFilterKeys.filter(
      (key) => key !== 'Routes' && key !== 'Destinations',
    ) as FilterPillKey[];

    filterKeys.forEach((key) => {
      const { list, getList } = FILTER_PILLS[key];
      // if the filter requires an api call, load it
      if (getList) {
        if (key === 'Filtered Demand Sources') {
          apiCalls.push(
            getFilteredDemandSourcesWithBidderTypes(
              session,
              user,
              '',
              !!overviewCfg.defaultState.activeFilters.device,
            ),
          );
        } else {
          apiCalls.push(getList(session, user));
        }
      } else if (list) {
        updatedFilters[key] = FILTER_PILLS[key].list || ({} as IMenuList);
      }
    });
    Promise.all(apiCalls)
      .then((apiFilters) => {
        for (const filter of apiFilters) {
          updatedFilters = { ...updatedFilters, ...filter };
        }
        // if there are selected defaults, make them selected true here before continuing
        addDefaultSelected(filterKeys, updatedSelectedFilters, updatedFilters, overviewCfg.filters);
      })
      .then(() => {
        // inventory routes and destinations are handled separately because they're generated through a single api call
        generateInventoryRoutesAndDestinations(session.publisher.ID, user.user).then(
          ({ Routes, Destinations }) => {
            updatedFilters.Routes = Routes;
            updatedFilters.Destinations = Destinations;
            // if there are selected defaults, make them selected true here before continuing
            addDefaultSelected(
              ['Routes', 'Destinations'] as FilterPillKey[],
              updatedSelectedFilters,
              updatedFilters,
              overviewCfg.filters,
            );

            const timezones = genTimezoneList(timezone);
            updatedFilters.Timezones = timezones;

            unstable_batchedUpdates(() => {
              setFilters(updatedFilters);
              setSelectedFilters(updatedSelectedFilters);
              setFiltersLoadState(LoadingStateHasData);
            });
          },
        );
      })
      .catch((err) => {
        console.error(err);
        setFiltersLoadState(LoadingStateNoData);
      });
  };

  const handleAcceptFilter = async (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    setLoadState(LoadingStateLoading);
    const appliedFilter = overviewCfg.filters[key];
    const { category } = appliedFilter;
    const clonedActiveFilters = cloneDeep(activeFilters);
    if (!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (appliedFilter.isGroup) appliedFilter.defaultGroupSelected = [];
    } else if (!!selectedFilters[key] && isEmpty(selectedOptions)) {
      appliedFilter.defaultSelected = [];
      if (appliedFilter.isGroup) appliedFilter.defaultGroupSelected = [];
      if (category && clonedActiveFilters[category] === key) {
        clonedActiveFilters[category] = '';
      }
    } else {
      appliedFilter.defaultSelected = Object.values(selectedOptions).map((item) => item.ID);
      if (category) clonedActiveFilters[category] = key;
    }

    // custom block for refreshing bidders & demand sources
    const updatedSelectedFilters = {} as Record<string, IMenuList>;
    const updatedFilters = {} as Record<string, IMenuList>;
    if (key === 'Bidder Groups') {
      const bidderIDs = formatSelectedBidderGroupIntoStringOfIDs(selectedOptions);
      appliedFilter.defaultGroupSelected = bidderIDs.split(',');
    }
    if (category === 'supply' || category === 'device') {
      unstable_batchedUpdates(() => {
        // we set this early here to prevent a color change delay while the demand dropdowns refresh
        setActiveFilters(clonedActiveFilters);
        setIsDemandRefreshing(true);
      });
      let siteIDs = '';
      switch (clonedActiveFilters.supply) {
        case 'Channels':
          siteIDs = Object.keys(selectedOptions).join(',');
          break;
        case 'Channel Groups':
          siteIDs = formatSelectedChannelGroupIntoStringOfIDs(selectedOptions);
          appliedFilter.defaultGroupSelected = siteIDs.split(',');
          break;
        case 'Routes':
          siteIDs = formatSelectedRouteIntoStringOfSiteIDs(selectedOptions);
          break;
        case 'Destinations':
          siteIDs = formatSelectedDestinationsIntoStringOfSiteIDs(selectedOptions);
          break;
        default:
          break;
      }
      if (category === 'supply') {
        const results = await Promise.all([
          refreshBiddersFilter(session, user, selectedFilters.Bidders, siteIDs),
          refreshDemandSourcesFilter(
            session,
            user,
            selectedFilters['Filtered Demand Sources'],
            siteIDs,
          ),
        ]);
        const { selectedBidders, allBidders } = results[0];
        const { selectedDemandSources, allDemandSources } = results[1];
        updatedSelectedFilters.Bidders = selectedBidders;
        updatedFilters.Bidders = allBidders;
        if (isEmpty(selectedBidders) && activeFilters.demand === 'Bidders') {
          clonedActiveFilters.demand = '';
        }
        updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
        updatedFilters['Filtered Demand Sources'] = allDemandSources;
        if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
          clonedActiveFilters.demand = '';
        }
      }
      if (category === 'device') {
        const result = await refreshDemandSourcesFilter(
          session,
          user,
          selectedFilters['Filtered Demand Sources'],
          siteIDs,
          !isEmpty(selectedOptions),
        );
        const { selectedDemandSources, allDemandSources } = result;
        updatedSelectedFilters['Filtered Demand Sources'] = selectedDemandSources;
        updatedFilters['Filtered Demand Sources'] = allDemandSources;
        if (isEmpty(selectedDemandSources) && activeFilters.demand === 'Filtered Demand Sources') {
          clonedActiveFilters.demand = '';
        }
      }
    }

    // update states
    unstable_batchedUpdates(() => {
      setFilters({ ...filters, [key]: allOptions, ...updatedFilters });
      setSelectedFilters((selectedFilters) => ({
        ...selectedFilters,
        [key]: selectedOptions,
        ...updatedSelectedFilters,
      }));

      setActiveFilters({ ...clonedActiveFilters });
      setIsDemandRefreshing(false);
    });
    overviewCfg.defaultState.activeFilters = clonedActiveFilters;
    track('Dash Live - Use Filter', { isSuperUser, filter: key }, user.user);
  };

  const handleClearAllFilters = async () => {
    setLoadState(LoadingStateLoading);
    const clonedFilters = cloneDeep(filters);
    // if a supply filter was active, we must reload demand sources and bidders
    if (activeFilters.supply || activeFilters.device) {
      setIsDemandRefreshing(true);
      await Promise.all([
        getFilteredDemandSourcesWithBidderTypes(session, user),
        getSmallBiddersList(session, user),
      ]).then((result) => {
        clonedFilters['Filtered Demand Sources'] = result[0]['Filtered Demand Sources'];
        selectedFilters['Filtered Demand Sources'] = {};
        clonedFilters.Bidders = result[1].Bidders;
        selectedFilters.Bidders = {};
      });
    }
    const selectedFilterKeys = Object.keys(selectedFilters);
    selectedFilterKeys.forEach((filterKey) => {
      if (filterKey === 'Timezones') return;
      const selectedItemKeys = Object.keys(selectedFilters[filterKey]);
      selectedItemKeys.forEach((key) => {
        clonedFilters[filterKey][key].selected = false;
      });
      overviewCfg.filters[filterKey].defaultSelected = [];
    });
    unstable_batchedUpdates(() => {
      setFilters(clonedFilters);
      setSelectedFilters({} as Record<string, IMenuList>);
      setActiveFilters(clearedActiveFilters);
      setIsDemandRefreshing(false);
    });
    isDemandSourceUsingBidderType.current = false;
    overviewCfg.defaultState.activeFilters = clearedActiveFilters;
  };

  const handleChangeTimezone = (
    _key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => {
    if (isEmpty(selectedOptions)) return;

    const updatedTimezone = Object.values(selectedOptions)[0].ID;
    if (updatedTimezone === timezone) return;

    const clonedFilters = cloneDeep(filters);
    clonedFilters.Timezones = allOptions;

    unstable_batchedUpdates(() => {
      setTimezone(updatedTimezone);
      setFilters({ ...filters, Timezones: allOptions });
      setSelectedFilters({ ...selectedFilters, Timezones: selectedOptions });
    });

    overviewCfg.defaultState.timezone = updatedTimezone;
  };

  const handleOpenFilter = () => {
    setIsDataRefreshActive(false);
  };

  const handleCloseFilter = (hasChangeOccurred: boolean) => {
    if (!hasChangeOccurred) setIsDataRefreshActive(true);
  };

  const handleOnChangeCardMetric = (
    metricKey: string,
    index: number,
    isDisplayedOnChart: boolean = false,
  ) => {
    let updatedActiveChartMetrics: IDashboardChartMetric[];
    const clonedDisplayedMetricKeys = [...displayedMetricKeys];
    // if active, replace active metric
    if (isDisplayedOnChart) {
      const clonedActiveChartMetrics = [...activeChartMetrics];
      const prevMetric = clonedDisplayedMetricKeys[index];
      updatedActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== prevMetric,
      );
      const key = metricKey as keyof IDashboardHomeCardMetrics;
      const selectedMetric = overviewCfg.cards.metrics[key];
      updatedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: index,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
    }
    clonedDisplayedMetricKeys[index] = metricKey as keyof IDashboardHomeCardMetrics;
    unstable_batchedUpdates(() => {
      setDisplayedMetricKeys(clonedDisplayedMetricKeys);
      if (isDisplayedOnChart) {
        setActiveChartMetrics(updatedActiveChartMetrics);
        defaultState.activeChartMetrics = updatedActiveChartMetrics;
      }
    });
    defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
    overviewCfg.defaultState.displayedMetricKeys = clonedDisplayedMetricKeys;
    track('Dash Live - Change Card Metric', { isSuperUser, metric: metricKey }, user.user);
  };

  const handleOnClickCard = (metricKey: string, cardIndex: number) => {
    const key = metricKey as keyof IDashboardHomeCardMetrics;
    const clonedActiveChartMetrics = [...activeChartMetrics];
    const metric = find(clonedActiveChartMetrics, { key });
    if (!metric) {
      const selectedMetric = overviewCfg.cards.metrics[key];
      clonedActiveChartMetrics.push({
        key: metricKey,
        label: selectedMetric.label,
        valueType: selectedMetric.valueType,
        chartColorIndex: cardIndex,
        chartType: selectedMetric.chartType,
        yScale: selectedMetric.yScale || 'base',
      });
      setActiveChartMetrics(clonedActiveChartMetrics);
      defaultState.activeChartMetrics = clonedActiveChartMetrics;
      overviewCfg.defaultState.activeChartMetrics = clonedActiveChartMetrics;
    } else {
      const filteredActiveChartMetrics = clonedActiveChartMetrics.filter(
        (metric) => metric.key !== key,
      );
      setActiveChartMetrics(filteredActiveChartMetrics);
      defaultState.activeChartMetrics = filteredActiveChartMetrics;
      overviewCfg.defaultState.activeChartMetrics = filteredActiveChartMetrics;
    }
    track('Dash Live - Display Metric On Chart', { isSuperUser, metric: metricKey }, user.user);
  };

  const handlePeriodMenuToggle = () => {
    overviewCfg.defaultState.isPeriodMenuOpen = !isPeriodMenuOpen;
    setIsPeriodMenuOpen(!isPeriodMenuOpen);
    track(
      'Dash Live - Open/Close Period Drawer',
      { isSuperUser, open: !isPeriodMenuOpen },
      user.user,
    );
  };

  const handleFilterMenuToggle = () => {
    overviewCfg.defaultState.isFilterMenuOpen = !isFilterMenuOpen;
    setIsFilterMenuOpen(!isFilterMenuOpen);
    track(
      'Dash Live - Open/Close Filter Drawer',
      { isSuperUser, open: !isFilterMenuOpen },
      user.user,
    );
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding noVerticalPadding>
        <Cols wrap gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col style={{ minWidth: '248px' }}>
            <DashboardSelector />
          </Col>
          <Col>
            <Cols gap={spacing.extraLarge} style={{ alignItems: 'center' }}>
              <Col>
                <DashboardFilterMenuToggle
                  activeFilters={activeFilters}
                  handleOnClick={handleFilterMenuToggle}
                  isOpen={isFilterMenuOpen}
                />
              </Col>
              <Col>
                <MenuToggle
                  isActive={isPeriodMenuOpen}
                  onClick={handlePeriodMenuToggle}
                  value={timeRange.label}
                  tKey={timeRange.tKey}
                  SvgIcon={ClockIcon}
                  style={{ height: '30px' }}
                />
              </Col>
              <Col>
                {isSuperUser ? (
                  <IconTextToggle
                    label='Auto-Refresh'
                    tKey='AutoRefresh'
                    handleOnClick={() => setIsDataRefreshActive(!isDataRefreshActive)}
                    isActive={isDataRefreshActive}
                    iconSlug={isDataRefreshActive ? 'refresh' : 'pause'}
                    defaultColor={publicaColors.gray}
                    defaultBackgroundColor={publicaColors.lightGray}
                    hoverColor={publicaColors.darkGray}
                    buttonStyle={{ padding: '6px 14px 6px 12px' }}
                    tooltip={
                      <>
                        <Text
                          bold
                          mini
                          color={publicaColors.white}
                          tKey={
                            isDataRefreshActive
                              ? 'DashboardTooltipClickToPause'
                              : 'DashboardTooltipClickToStart'
                          }
                        />
                        <Text
                          bold
                          mini
                          color={publicaColors.white}
                          tKey={'DashboardTooltipLastUpdatedTime'}
                          tProps={{ timestamp: lastRefreshedTime }}
                        >
                          Last Updated: {lastRefreshedTime}
                        </Text>
                      </>
                    }
                  />
                ) : (
                  <StaticAutoRefresh lastRefreshedTime={lastRefreshedTime} />
                )}
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{ display: isPeriodMenuOpen || isFilterMenuOpen ? 'block' : 'none' }}
      >
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isPeriodMenuOpen ? 'block' : 'none' }}
          >
            <Cols
              extraSmallVerticalPadding
              gap={spacing.extraLarge}
              style={{ alignItems: 'center' }}
            >
              <Col>
                <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                  <Col displayFlex>
                    <ClockIcon color={publicaColors.veryDarkGray} style={{ height: '14px' }} />
                  </Col>
                  <Col>
                    <Text small bold>
                      {t('Period')}
                    </Text>
                  </Col>
                </Cols>
              </Col>
              <Col>
                <DateTimeRangeToggles
                  ranges={defaultTimeRanges}
                  activeKey={timeRange.key}
                  onClick={handleToggleTimeRange}
                />
              </Col>
            </Cols>
          </Row>
          <Row
            horizontalPadding
            extraSmallVerticalPadding
            style={{ display: isFilterMenuOpen ? 'block' : 'none' }}
          >
            <DashboardFilters
              activeFilters={activeFilters}
              filters={filters}
              filtersCfg={overviewCfg.filters}
              handleAcceptFilter={handleAcceptFilter}
              handleChangeTimezone={handleChangeTimezone}
              handleClearAllFilters={handleClearAllFilters}
              isDemandRefreshing={isDemandRefreshing}
              loadState={filtersLoadState}
              orderedFilterKeys={liveCfg.orderedFilterKeys as FilterPillKey[]}
              selectedFilters={selectedFilters}
              timezone={timezone}
              isLive={true}
              handleOpenFilter={handleOpenFilter}
              handleCloseFilter={handleCloseFilter}
              isDemandSourceUsingBidderType={isDemandSourceUsingBidderType.current}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding enableSize>
        <Rows
          style={{
            border: `1px solid ${publicaColors.lightGray}`,
            borderRadius: '4px',
            backgroundColor: publicaColors.white,
          }}
        >
          <Row style={{ padding: '8px 16px 0px' }}>
            <Text small bold tKey='Performances'>
              Performances
            </Text>
          </Row>
          <Row style={{ padding: '8px 16px 16px' }}>
            <DashboardCardsV2
              availableMetrics={extractViableLiveMetrics(
                overviewCfg.cards,
                liveCfg.cards.groupings[cardGroup],
              )}
              displayedMetricKeys={displayedMetricKeys}
              cardMetrics={data.CardMetrics}
              loadState={loadState}
              activeChartMetrics={activeChartMetrics}
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Row>
          <Row>
            <CompareChart
              loadState={loadState}
              data={mergeCompareData(data.LiveData)}
              metrics={activeChartMetrics}
              metricsConfig={overviewCfg.cards.metrics}
              grouping={0}
              syncID={chartSyncID}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel largeHorizontalPadding smallVerticalPadding enableSize>
        <Rows gap={spacing.base}>
          {subCharts.map((chart) => {
            return (
              <Row
                noHorizontalPadding
                extraSmallVerticalPadding
                key={chart.title}
                style={{
                  border: `1px solid ${publicaColors.lightGray}`,
                  borderRadius: '4px',
                  backgroundColor: publicaColors.white,
                }}
              >
                <DashboardSubChart
                  title={chart.title}
                  tKey={chart.tKey}
                  loadState={loadState}
                  data={extractDataForMultiLineChart(
                    data.LiveData || [],
                    Object.keys(chart.metrics),
                  )}
                  metricsCfg={chart.metrics}
                  orderedMetricKeys={chart.orderedMetricKeys}
                  activeFilters={activeFilters}
                  syncID={chartSyncID}
                />
              </Row>
            );
          })}
        </Rows>
      </Panel>
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
